import { applySnapshot, getSnapshot, types } from "mobx-state-tree"
import { flow } from "modules/common/models/flow"
import { Notificator } from "modules/common/models/notificator"
import { Queryable } from "modules/common/models/queryable"
import { Transport } from "modules/common/models/transport"
import { apiUrls } from "modules/common/services/communication/urls"
import { nameof } from "modules/common/services/typescript"
import { texts } from "modules/common/texts"

/** Типы запросов, приходящих с бэкенда */
export const RequestTypes = {
    confirm: "confirm",
    agent: "agent",
    orderRemove: "orderRemove",
    agentRequest: "agentRequest",
}

/** Решённый запрос сразу убираем из таблицы: счётчик на вкладке считается по этому же списку */
const removeRequest = (self: { data: MainPageRequestItemType[] }, requestId: string) => {
    const index = self.data.findIndex((d) => d.id === requestId)
    if (index > -1) {
        self.data.splice(index, 1)
    }
}

const MainPageRequestItem = types
    .model({
        id: types.string,
        type: types.string,
        targetId: types.string,
        dateStart: types.string,
        inventoryNumber: types.string,
        targetName: types.string,
        orderStatus: types.string,
        orderStatusColor: types.string,
        title: types.string,
        authorName: types.string,
    })
    .named("MainPageRequestItem")

export const MainPageRequestsStore = types
    .compose(
        Transport,
        Notificator,
        Queryable,
        types.model({
            data: types.array(MainPageRequestItem),
            loading: types.boolean,
            /**
             * Сотрудник решает запросы: вкладку показываем ему и с пустым списком.
             * Считает сервер — ГИПу подтверждения приходят без отдельного права
             */
            visible: types.boolean,
        })
    )
    .views((self) => ({
        /** Сколько запросов ждёт решения: число на кнопке вкладки */
        get count() {
            return self.data.length
        },

        get mainData() {
            const predicate = (o: MainPageRequestItemType) => {
                let result = true
                if (self.pureQuery) {
                    result =
                        result &&
                        `${o.inventoryNumber} ${o.targetName} ${o.orderStatus} ${o.title} ${o.authorName} ${o.dateStart}`
                            .toLowerCase()
                            .indexOf(self.pureQuery) >= 0
                }

                return result
            }

            return self.data.filter(predicate).map((request) => ({
                ...getSnapshot(request),
            }))
        },
    }))
    .actions((self) => ({
        load: flow(function* () {
            try {
                self.loading = true
                const newData = yield self.transport.get<any>(apiUrls.orders.mainPage.requests)

                applySnapshot(self.data, newData.items)
                self.visible = newData.visible

                return true
            } catch (er) {
                self.notify.error(er)
                return false
            } finally {
                self.loading = false
            }
        }),

        /** Согласиться с запросом на удаление проекта */
        removeOrder: flow(function* (id: string, requestId: string) {
            try {
                self.loading = true
                const res = yield self.transport.post<boolean>(apiUrls.orders.summary.remove(id))

                if (res === true) {
                    self.notify.success(texts.messages.removed)
                    const index = self.data.findIndex((d) => d.id === requestId)
                    if (index > -1) {
                        self.data.splice(index, 1)
                    }
                }

                return true
            } catch (er) {
                self.notify.error(er)
                return false
            } finally {
                self.loading = false
            }
        }),

        /** Отклонить запрос на удаление проекта */
        cancelOrderRemove: flow(function* (id: string, requestId: string) {
            try {
                self.loading = true
                const res = yield self.transport.delete<any>(apiUrls.orders.summary.cancel(id))

                if (res) {
                    const index = self.data.findIndex((d) => d.id === requestId)
                    if (index > -1) {
                        self.data.splice(index, 1)
                    }
                }

                return true
            } catch (er) {
                self.notify.error(er)
                return false
            } finally {
                self.loading = false
            }
        }),

        /** Подтвердить загруженную версию проекта */
        confirmOrderUpload: flow(function* (id: string, requestId: string) {
            try {
                self.loading = true
                const res = yield self.transport.post<boolean>(apiUrls.orders.mainPage.confirmUpload(id), {})

                if (res) {
                    self.notify.success("Загрузка проекта подтверждена")
                    removeRequest(self, requestId)
                }

                return true
            } catch (er) {
                self.notify.error(er)
                return false
            } finally {
                self.loading = false
            }
        }),

        /** Отклонить запрос на подтверждение проекта */
        rejectOrderUpload: flow(function* (id: string, requestId: string) {
            try {
                self.loading = true
                const res = yield self.transport.delete<boolean>(apiUrls.orders.mainPage.confirmUpload(id))

                if (res) {
                    removeRequest(self, requestId)
                }

                return true
            } catch (er) {
                self.notify.error(er)
                return false
            } finally {
                self.loading = false
            }
        }),

        /** Одобрить подрядчика */
        approveOutsourcer: flow(function* (id: string, requestId: string) {
            try {
                self.loading = true
                const res = yield self.transport.post<boolean>(apiUrls.outsourcers.approve(id), {})

                if (res) {
                    self.notify.success("Контрагент одобрен")
                    removeRequest(self, requestId)
                }

                return true
            } catch (er) {
                self.notify.error(er)
                return false
            } finally {
                self.loading = false
            }
        }),

        /** Отклонить запрос на одобрение подрядчика */
        rejectOutsourcer: flow(function* (id: string, requestId: string) {
            try {
                self.loading = true
                const res = yield self.transport.delete<boolean>(apiUrls.outsourcers.approve(id))

                if (res) {
                    removeRequest(self, requestId)
                }

                return true
            } catch (er) {
                self.notify.error(er)
                return false
            } finally {
                self.loading = false
            }
        }),

        /** Одобрить заявку на регистрацию от агента */
        approveAgentRequest: flow(function* (id: string, requestId: string) {
            try {
                self.loading = true
                const res = yield self.transport.post<boolean>(apiUrls.outsourcers.approveAgentRequest(id), {})

                if (res) {
                    self.notify.success("Заявка одобрена")
                    removeRequest(self, requestId)
                }

                return true
            } catch (er) {
                self.notify.error(er)
                return false
            } finally {
                self.loading = false
            }
        }),

        /** Удалить заявку на регистрацию от агента */
        removeAgentRequest: flow(function* (id: string, requestId: string) {
            try {
                self.loading = true
                const res = yield self.transport.delete<boolean>(apiUrls.outsourcers.removeRequestAgent(id))

                if (res) {
                    self.notify.success(texts.messages.removed)
                    removeRequest(self, requestId)
                }

                return true
            } catch (er) {
                self.notify.error(er)
                return false
            } finally {
                self.loading = false
            }
        }),
    }))
    .named("MainPageRequestsStore")

export type MainPageRequestsStoreType = typeof MainPageRequestsStore.Type
export type MainPageRequestsStoreSnapshotType = typeof MainPageRequestsStore.SnapshotType

export type MainPageRequestItemType = typeof MainPageRequestItem.Type
export type MainPageRequestItemSnapshotType = typeof MainPageRequestItem.SnapshotType

export const initialState = (): MainPageRequestsStoreSnapshotType => ({
    data: [],
    loading: false,
    visible: false,
    pureQuery: "",
    query: "",
})

export const fields = {
    inventoryNumber: nameof((a: MainPageRequestItemType) => a.inventoryNumber) as string,
    targetName: nameof((a: MainPageRequestItemType) => a.targetName) as string,
    orderStatus: nameof((a: MainPageRequestItemType) => a.orderStatus) as string,
    title: nameof((a: MainPageRequestItemType) => a.title) as string,
    authorName: nameof((a: MainPageRequestItemType) => a.authorName) as string,
    dateStart: nameof((a: MainPageRequestItemType) => a.dateStart) as string,
}
